import React, { FC, useEffect } from 'react'
import {
    DropResult,
} from 'react-beautiful-dnd'
import { useAppSelector } from '../../hooks/useAppSelector'
import { IProductGroup } from '../../types/product'
import { NoData } from '../NoData'
import { ProductsGroupContainer } from './Components/ProductsGroupContainer'
import { ProductsOthers } from './Components/ProductsOthers'
import {
    getGroupEditableFormat,
    getProductsList,
    reorder,
    reorderBetweenGroups,
} from './utils'
import { ProductStatus } from '../../entity/Product'
import { Button } from 'react-bootstrap'
import { useAppDispatch } from '../../hooks/useAppDispatch'
import { selectProducts, selectProductsGroups } from '../../store/selectors/productsSelectors'
import { deleteOneProduct } from '../../store/async-thunks/products/deleteOneProduct'
import { editProductsGroup } from '../../store/async-thunks/products/groups/editProductsGroup'
import { dndProductBetweenGroups } from '../../store/async-thunks/products/groups/dndProductBetweenGroups'
import { changeGroupsPositions } from '../../store/async-thunks/products/groups/changeGroupsPositions'
import {
    closestCenter,
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { rectSortingStrategy, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { GridDnD } from '../DnD/Grid'
import { MultipleContainers } from '../DnD/MultipleDnD'
import { productsActions } from '../../store/slices/productsSlice'

interface IProducts {
    hideInactiveState: { [key: string]: boolean }
}

export const Products: FC<IProducts> = ({ hideInactiveState }) => {
    const dispatch = useAppDispatch()

    const products = useAppSelector(selectProducts)
    const productsGroups = useAppSelector(selectProductsGroups)
    const productsList = getProductsList(products, hideInactiveState.showActive)

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        })
    )

    const deleteAllInactiveProducts = () => {
        products
            .filter((product) => product?.status === ProductStatus.INACTIVE)
            .forEach((product) =>
                dispatch(
                    deleteOneProduct({ guid: product?.guid, name: product?.name })
                )
            )
    }

    useEffect(() => {
        dispatch(productsActions.setIsProductRouted({ isProductRouted: true }))
    }, []) // eslint-disable-line

    const groupsIds: string[] = productsGroups.map(
        (group: any) => group.group_id
    )

    const handleOnDragEnd = (result: DropResult): void => {
        const { destination, source, type, draggableId } = result

        // return draggable to place if dropped out of droppable area
        if (!destination) return

        const sourceGroupId = source.droppableId
        const destinationGroupId = destination.droppableId
        const sourceIndex = source.index
        const destinationIndex = destination.index

        // no actions on dropping draggable to its previous position
        if (
            destinationGroupId === sourceGroupId &&
            destinationIndex === sourceIndex
        ) {
            return
        }

        const sourceGroup = productsGroups.find(
            (group: IProductGroup) => group.group_id === sourceGroupId
        )

        // drag and drop product inside the same group
        if (type === 'product' && destinationGroupId === sourceGroupId) {
            const reorderedProductsIds = reorder(
                sourceGroup?.entries || [],
                sourceIndex,
                destinationIndex
            )
            const formattedData = getGroupEditableFormat(
                sourceGroup,
                reorderedProductsIds,
                groupsIds
            )
            dispatch(
                editProductsGroup({
                    groupId: sourceGroupId,
                    data: formattedData,
                })
            )
        }

        // drag and drop product to another group
        if (type === 'product' && destinationGroupId !== sourceGroupId) {
            const reorderedGroups = reorderBetweenGroups(productsGroups, result)
            dispatch(
                dndProductBetweenGroups({
                    sourceGroupId,
                    destinationGroupId,
                    productId: draggableId,
                    reorderedGroups
                })
            )
        }

        // drag and drop groups themselves
        if (type === 'group') {
            const reorderedGroups: IProductGroup[] = reorder(
                productsGroups,
                sourceIndex,
                destinationIndex
            )
            const items: string[] = reorder(
                groupsIds,
                sourceIndex,
                destinationIndex
            )
            dispatch(
                changeGroupsPositions({ reorderedGroups, positions: items })
            )
        }
    }

    const handleDragEnd = (event: any) => {
        const { active, over } = event
        if (!over || active.id === over.id) return

        const [activeGroupId, activeProductId] = active.id.split(':')
        const [overGroupId, overProductId] = over.id.split(':')

        if (!activeProductId && !overProductId) {
            // Dragging groups
            const oldIndex = productsGroups.findIndex(
                (g) => g.group_id === activeGroupId
            )
            const newIndex = productsGroups.findIndex(
                (g) => g.group_id === overGroupId
            )
            const reorderedGroups: IProductGroup[] = reorder(
                productsGroups,
                oldIndex,
                newIndex
            )
            const items: string[] = reorder(groupsIds, oldIndex, newIndex)
            dispatch(changeGroupsPositions({ reorderedGroups, positions: items }))
        } else {
            // Dragging products
            const updatedGroups = productsGroups.slice()
            const sourceGroup = updatedGroups.find(
                (g) => g.group_id === activeGroupId
            )
            const targetGroup = updatedGroups.find(
                (g) => g.group_id === overGroupId
            )

            const productIndex = sourceGroup!.entries.findIndex(
                (pID) => pID === activeProductId
            )
            const [movedProduct] = sourceGroup!.entries.splice(productIndex, 1)

            const targetIndex = targetGroup!.entries.findIndex(
                (pID) => pID === overProductId
            )
            if (targetIndex >= 0) {
                targetGroup!.entries.splice(targetIndex, 0, movedProduct)
            } else {
                targetGroup!.entries.push(movedProduct)
            }

            // TODO fix products dnd btw groups
            // dispatch(
            //     dndProductBetweenGroups({
            //         sourceGroupId: activeGroupId,
            //         destinationGroupId: overGroupId,
            //         productId: activeProductId,
            //         reorderedGroups: updatedGroups,
            //     })
            // )
            // dispatch(
            //     dndProduct(
            //         activeGroupId,
            //         overGroupId,
            //         activeProductId,
            //         updatedGroups
            //     )
            // )
        }
    }

    return (
        <>
            {!products.length &&
            !productsList.length &&
            !productsGroups.length ? (
                <NoData />
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={groupsIds}
                        strategy={verticalListSortingStrategy}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {productsGroups.map(
                                (groupData: IProductGroup, index: number) => (
                                    <ProductsGroupContainer
                                        key={groupData.group_id}
                                        productsList={productsList}
                                        groupData={groupData}
                                        hideInactive={
                                            hideInactiveState.showActive
                                        }
                                        index={index}
                                    />
                                )
                            )}
                        </div>
                    </SortableContext>
                </DndContext>
            )}
            <ProductsOthers
                productsList={productsList}
                hideInactive={hideInactiveState.showActive}
            />
            {process.env.NODE_ENV === 'development' && (
                <>
                    <Button
                        className="ml-auto mr-auto"
                        onClick={deleteAllInactiveProducts}
                    >
                        Delete all inactive
                    </Button>
                    <div>
                        <GridDnD layoutType="grid" />
                    </div>
                    <div>
                        <MultipleContainers
                            columns={3}
                            strategy={rectSortingStrategy}
                            wrapperStyle={() => ({
                                width: 50,
                                height: 50,
                            })}
                        />
                    </div>
                </>
            )}
        </>
    )
}
